import React, { useState, useEffect } from 'react';
import './UploadBanners.scss';
import {
  Breadcrumbs,
  Typography,
  Link,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core';
import { CloudUpload, Close, Edit, Delete } from '@material-ui/icons';
import { http, useNavigationService, AlertService, localstorage } from '../../fx-core';

const IMAGE_BASE_URL = 'https://localitesstrg.blob.core.windows.net/commkit-ctn/';
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;charset=UTF-8,%3csvg width='800' height='400' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='800' height='400' fill='%23f0f0f0'/%3e%3ctext x='50%25' y='50%25' font-family='Arial,sans-serif' font-size='24' fill='%23999' text-anchor='middle' dy='.3em'%3eNo Image%3c/text%3e%3c/svg%3e";

const UploadBanners = (props: any) => {
  const { routeData, back } = useNavigationService();

  // Get communityId from routeData or fallback to localStorage
  const communityId = routeData?.communityId
    ? String(routeData.communityId)
    : localstorage.getItem('communityId') || '0';

  const communityName = routeData?.communityName || 'Community';

  // ============ STATE ============
  const [openBannerDialog, setOpenBannerDialog] = useState<boolean>(false);
  const [bannerFormData, setBannerFormData] = useState({
    bannerName: '',
    link: '',
    description: '',
    file: null as File | null,
    fileName: '',
    existingImagePath: ''
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [bannersList, setBannersList] = useState<any[]>([]);
  const [isLoadingBanners, setIsLoadingBanners] = useState<boolean>(false);
  const [editingBannerId, setEditingBannerId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [bannerToDelete, setBannerToDelete] = useState<number | null>(null);

  // ============ HELPERS ============
  const getImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath) return PLACEHOLDER_IMAGE;
    if (imagePath.startsWith('http')) return imagePath;
    return `${IMAGE_BASE_URL}${imagePath}`;
  };

  const navToBack = () => back();

  // ============ FETCH COMMUNITY BANNERS ============
  const fetchAllBanners = async () => {
    setIsLoadingBanners(true);
    try {
      // ✅ Fixed: matches renamed route api/banner/list/community/{communityId}
      const response = await http.get({ url: `banner/list/community/${communityId}` });
      if (response && response.data && response.data.banners) {
        setBannersList(response.data.banners);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      AlertService.showErrorMsg('Failed to load banners');
    } finally {
      setIsLoadingBanners(false);
    }
  };

  useEffect(() => {
    fetchAllBanners();
  }, []);

  // ============ BANNER DIALOG HANDLERS ============
  const handleOpenBannerDialog = () => {
    setEditingBannerId(null);
    setBannerFormData({
      bannerName: '',
      link: '',
      description: '',
      file: null,
      fileName: '',
      existingImagePath: ''
    });
    setOpenBannerDialog(true);
  };

  const handleEditBanner = (banner: any) => {
    setEditingBannerId(banner.id);
    setBannerFormData({
      bannerName: banner.bannerName || '',
      link: banner.link || '',
      description: banner.description || '',
      file: null,
      fileName: '',
      existingImagePath: banner.imagePath || ''
    });
    setOpenBannerDialog(true);
  };

  const handleCloseBannerDialog = () => {
    setOpenBannerDialog(false);
    handleBannerClear();
    setEditingBannerId(null);
  };

  const handleBannerInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setBannerFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFormData(prev => ({ ...prev, file, fileName: file.name }));
    }
  };

  // ============ SUBMIT: CREATE & UPDATE ============
  const handleBannerSubmit = async () => {
    if (!bannerFormData.bannerName) {
      AlertService.showErrorMsg('Banner name is required');
      return;
    }
    if (!editingBannerId && !bannerFormData.file) {
      AlertService.showErrorMsg('Please select an image file for new banner');
      return;
    }

    try {
      setIsSubmitting(true);

      const uploadData = new FormData();
      if (bannerFormData.file) {
        uploadData.append('file', bannerFormData.file);
      }
      uploadData.append('bannerName', bannerFormData.bannerName);
      uploadData.append('link', bannerFormData.link || '');
      uploadData.append('description', bannerFormData.description || '');
      uploadData.append('communityId', communityId);

      if (editingBannerId) {
        // UPDATE existing banner
        uploadData.append('id', editingBannerId.toString());
        await http.put({
          url: 'banner/saveimage',
          data: uploadData,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        AlertService.showSuccessMsg('Banner updated successfully');
      } else {
        // CREATE new community banner
        await http.post({
          url: 'banner/community/savewithimage',
          data: uploadData,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        AlertService.showSuccessMsg('Banner created successfully');
      }

      handleBannerClear();
      const fileInput = document.getElementById('banner-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setOpenBannerDialog(false);
      setEditingBannerId(null);
      fetchAllBanners();
    } catch (error) {
      console.error('Error creating/updating banner:', error);
      AlertService.showErrorMsg('Failed to save banner. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============ DELETE ============
  const handleDeleteBanner = (bannerId: number) => {
    setBannerToDelete(bannerId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteBanner = async () => {
    if (bannerToDelete !== null) {
      try {
        await http.delete({ url: `banner/community/delete/${bannerToDelete}` });
        AlertService.showSuccessMsg('Banner deleted successfully');
        fetchAllBanners();
      } catch (error) {
        console.error('Error deleting banner:', error);
        AlertService.showErrorMsg('Failed to delete banner');
      } finally {
        setDeleteDialogOpen(false);
        setBannerToDelete(null);
      }
    }
  };

  const cancelDeleteBanner = () => {
    setDeleteDialogOpen(false);
    setBannerToDelete(null);
  };

  const handleBannerClear = () => {
    setBannerFormData({
      bannerName: '',
      link: '',
      description: '',
      file: null,
      fileName: '',
      existingImagePath: ''
    });
    const fileInput = document.getElementById('banner-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const isCreateBannerDisabled = (): boolean => {
    if (!bannerFormData.bannerName) return true;
    if (!editingBannerId && !bannerFormData.file) return true;
    return false;
  };

  // ============ RENDER ============
  return (
    <div className="uploadbanners-ui pt-5">
      <div className="row title-font custom-head pl-4">
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="primary"
            className="custom-link pointer breadcrumbs-style"
            onClick={navToBack}
          >
            <b>{communityName}</b>
          </Link>
          <Typography color="primary" className="breadcrumbs-style">
            Banners
          </Typography>
        </Breadcrumbs>
      </div>

      <div className="content-container">
        <div className="content">
          <div className="promotion-banner-section">
            <div className="banners-header">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleOpenBannerDialog}
                className="create-banner-btn"
              >
                + Create New Banner
              </Button>
            </div>

            {isLoadingBanners ? (
              <div className="text-center p-5">
                <Typography variant="h6" style={{ color: '#666' }}>
                  Loading banners...
                </Typography>
              </div>
            ) : bannersList.length === 0 ? (
              <div className="empty-state p-5">
                <h4 className="text-center">No banners yet</h4>
                <p className="text-center text-muted">
                  Create your first promotion banner
                </p>
              </div>
            ) : (
              <div className="banners-list p-4">
                <TableContainer component={Paper}>
                  <Table size="small" className="table-striped">
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Banner Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Link</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bannersList.map((banner: any) => (
                        <TableRow key={banner.id}>
                          <TableCell>
                            <img
                              src={getImageUrl(banner.imagePath)}
                              alt={banner.bannerName}
                              style={{
                                width: '100px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                              onError={(e: any) => { e.target.src = PLACEHOLDER_IMAGE; }}
                            />
                          </TableCell>
                          <TableCell>{banner.bannerName}</TableCell>
                          <TableCell>{banner.description || '-'}</TableCell>
                          <TableCell>
                            {banner.link ? (
                              <a href={banner.link} target="_blank" rel="noopener noreferrer">
                                {banner.link}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditBanner(banner)}
                              title="Edit"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBanner(banner.id)}
                              title="Delete"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ CREATE/EDIT DIALOG ============ */}
      <Dialog
        open={openBannerDialog}
        onClose={handleCloseBannerDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="d-flex justify-content-between align-items-center">
            <span>{editingBannerId ? 'Edit Banner' : 'Create Banner'}</span>
            <IconButton size="small" onClick={handleCloseBannerDialog}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          {editingBannerId && bannerFormData.existingImagePath && (
            <div className="mb-3">
              <Typography variant="subtitle2" gutterBottom>Current Image:</Typography>
              <img
                src={getImageUrl(bannerFormData.existingImagePath)}
                alt="Current banner"
                style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px' }}
                onError={(e: any) => { e.target.src = PLACEHOLDER_IMAGE; }}
              />
              <Typography variant="caption" display="block" style={{ color: '#888', marginTop: 4 }}>
                Upload a new image below to replace this one (optional)
              </Typography>
            </div>
          )}

          <TextField
            name="bannerName"
            label="Banner Name *"
            variant="outlined"
            fullWidth
            value={bannerFormData.bannerName}
            onChange={handleBannerInputChange}
            placeholder="e.g., Diwali Sale"
            margin="normal"
          />

          <TextField
            name="link"
            label="Banner Link (URL)"
            variant="outlined"
            fullWidth
            value={bannerFormData.link}
            onChange={handleBannerInputChange}
            placeholder="https://localites.in/deals"
            margin="normal"
          />

          <TextField
            name="description"
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={bannerFormData.description}
            onChange={handleBannerInputChange}
            placeholder="Up to 50% off"
            margin="normal"
          />

          <div className="mt-3">
            <input
              id="banner-file-input"
              type="file"
              accept="image/*"
              onChange={handleBannerFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="banner-file-input" className="w-100">
              <Button
                variant="outlined"
                color="primary"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
              >
                {editingBannerId
                  ? bannerFormData.fileName || 'Change Banner Image (Optional)'
                  : bannerFormData.fileName || 'Upload Banner Image *'}
              </Button>
            </label>
            {bannerFormData.fileName && (
              <div className="mt-2" style={{ color: '#666', fontSize: 13 }}>
                ✓ New file selected: <strong>{bannerFormData.fileName}</strong>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className="p-3">
          <Button onClick={handleCloseBannerDialog} variant="outlined">Cancel</Button>
          <Button
            onClick={handleBannerSubmit}
            variant="contained"
            color="primary"
            disabled={isCreateBannerDisabled() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : editingBannerId ? 'Update Banner' : 'Create Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DELETE DIALOG ============ */}
      <Dialog open={deleteDialogOpen} onClose={cancelDeleteBanner} maxWidth="xs" fullWidth>
        <DialogTitle><b>Delete Banner</b></DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this banner?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteBanner} variant="outlined">No</Button>
          <Button onClick={confirmDeleteBanner} variant="contained" color="secondary">Yes</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UploadBanners;